<script setup async>
import {competitionFacts} from '~/utils/privateAccess';
const {outputDateFromDb} = useCrudConverters();
import {computed} from "vue";
import {createError} from 'nuxt/app';
import {useHeadService} from '~/composables/useHeadService';
import {useAuthService} from '~/composables/useAuthService';
import {useStatsPage} from '~/composables/useStatsPage';
import FrontendHeader from "~/components/layout/FrontendHeader.vue";
import RequestAlert from "~/components/clubs/RequestAlert.vue";
import ClubChip from "~/components/clubs/ClubChip.vue";
import StatsView from "~/components/stats/StatsView.vue";
import ShortLinkShareWrapper from "~/components/share/ShortLinkShareWrapper.vue";
import ArchivedNotice from "~/components/ArchivedNotice.vue";

definePageMeta({
  name: 'LadderStats',
  path: '/ladders/:ladderId/stats',
});

const route = useRoute();
const router = useRouter();
const ladderId = route.params.ladderId;

// Fetch ladder information
const {item: ladder, getItem: getLadder, hasItemErrors: hasLadderErrors} = useCrudApi("api/v2/ladders");
await getLadder(ladderId);

if (hasLadderErrors.value) {
  throw createError({statusCode: 404, message: 'Ladder not found'});
}

const {stats} = await useStatsPage(
    'ladder',
    ladderId,
    'api/v2/ladders',
    {ladderId: ladderId},
    'ladderChallengesUpdated',
);

const {setMeta} = useHeadService();
setMeta(
    `${ladder.value.clubName} / ${ladder.value.name} - Stats on Tap Leagues`,
    `View detailed stats for ${ladder.value.name} at ${ladder.value.clubName} on Tap Leagues.`
);

const {isAdminOfClub} = useAuthService();
const isAdmin = computed(() => isAdminOfClub(ladder.value?.clubId));

// The ladder has been deleted (archived) - show the deleted notice instead of content.
const ladderArchived = computed(() => !!ladder.value?.archivedAt);
const ladderPrivate = computed(() => !!ladder.value?.privateToViewer);
usePrivatePage(ladderPrivate);

const titleActions = computed(() => isAdmin.value
    ? [{
      title: 'Edit ladder',
      icon: 'mdi-pencil',
      click: () => router.push({name: 'ClubLadderSettings', params: {clubId: ladder.value.clubId, ladderId: ladder.value.id}}),
      color: 'info',
    }]
    : []);
</script>

<template>
  <base-page>
    <!-- Deleted (archived) ladder - show the notice instead of any content -->
    <!-- Members only for this viewer (PRIVACY_PLAN.md §4.1): the stub keeps the name and the join link -->
    <!-- Members only for this viewer (PRIVACY_PLAN.md §4.1): identity only, then the notice where the content would be -->
    <v-container v-if="ladderPrivate" class="large">
      <frontend-header :ladder="ladder"
          :club="ladder.club ?? (ladder.clubId ? {id: ladder.clubId, slug: ladder.clubId, name: ladder.clubName} : null)"
          :title="`${ladder.name} - Stats`"
          title-icon="mdi-ladder"
          title-icon-color="ladder"
      >
        <template #subTitle>
          <club-chip v-if="ladder.club" :club="ladder.club"></club-chip>
          <span v-else-if="ladder.clubName" class="tl-chip">{{ ladder.clubName }}</span>
        </template>
      </frontend-header>
      <div class="tl-action-strip tl-action-strip--solo my-5">
        <short-link-share-wrapper>
          <template #default="{ click, loading: shareLoading }">
            <button class="as" @click="click" :disabled="shareLoading">
              <v-icon size="17" icon="mdi-share-variant"/>Share
            </button>
          </template>
        </short-link-share-wrapper>
      </div>
      <private-notice reason="competition" entity="ladder" :name="ladder.name" :club-name="ladder.clubName" :join-slug="ladder.visibleInviteLink?.slug"
                      :facts="competitionFacts('ladder', ladder, d => outputDateFromDb(d, 'd MMM yyyy'))"/>
    </v-container>
    <v-container v-else-if="ladderArchived" class="large">
      <archived-notice entity="ladder" :name="ladder.name"/>
    </v-container>
    <template v-else>
    <v-container class="large">
      <frontend-header
          :club="ladder.club"
          :ladder="ladder"
          title-icon="mdi-ladder"
          title-icon-color="ladder"
          :title="`${ladder.name} - Stats`"
          :actions="titleActions"
      >
        <template #subTitle>
          <club-chip :club="ladder.club"></club-chip>
        </template>
      </frontend-header>

      <div class="tl-action-strip mt-4">
        <short-link-share-wrapper>
          <template #default="{ click, loading: shareLoading }">
            <button class="as" @click="click" :disabled="shareLoading">
              <v-icon size="17" icon="mdi-share-variant"/>Share
            </button>
          </template>
        </short-link-share-wrapper>

        <router-link class="as" :to="{ name: 'LadderDetail', params: { ladderId: ladder.id } }">
          <v-icon size="17" icon="mdi-ladder"/>Ladder
        </router-link>

        <router-link class="as" :to="{ name: 'FixtureSearch', query: { clubIds: [ladder.clubId], ladderIds: [ladder.id] } }">
          <v-icon size="17" icon="mdi-scoreboard"/>Matches
        </router-link>
      </div>
    </v-container>

    <v-container class="large" v-if="stats && stats.length">
      <request-alert class="mb-6" :id="ladderId" namespace="ladders"/>

      <stats-view :stats="stats" form-mode="recent"/>
    </v-container>
    </template>
  </base-page>
</template>
